import { catalogApiClient } from "./catalogApiClient";
import { CaseLockControllerApi, CaseWorkflowStatusControllerApi } from "@/lib/api/generated/casedb";
import { generateUuid } from "@/lib/uuid";
import { CATALOG_ERROR_CODES } from "../utils/catalogErrors";

/**
 * Case-db edit-lock API client (issue #1698). Split out from `caseReview.ts`
 * (which is past the 1000-line TS soft cap) — see CLAUDE.md "File Size and
 * Organization". Same topic-split pattern as `caseReview.phaseC.test.ts` etc.
 * Over the generated client (`CaseLockControllerApi`, `CaseWorkflowStatusControllerApi`
 * in `src/lib/api/generated/casedb/api.ts`) rather than hand-built axios calls,
 * matching `catalogSectionService.ts`.
 *
 * Consumers:
 *   - work-queue/page.tsx → handleGoToCase (acquire on click)
 *   - case-review-shell/CaseReviewPage.tsx → lazy-lock useEffect (acquire on mount)
 *   - LockConflictModal admin Override (override + retry)
 *   - caseReview.ts:routeToNext (shares getOrCreateSessionId so the engine
 *     recognizes the same session that holds the lock)
 */

export interface LockHolder {
  lockedBy: number;
  lockedByName: string;
  expiresAt: string;
}

export type LockResult =
  | { ok: true }
  | { ok: false; holder: LockHolder | null; message: string };

/** Result of a heartbeat `refreshEditLock` call (issue #1757 / #1980). */
export type RefreshLockResult =
  | { ok: true }
  | { ok: false; message: string };

/**
 * Structured `errorCode` values the case-db lock/transition endpoints return
 * on 409 (see CaseLockedException / LockNotHeldException server-side).
 */
export const LOCK_ERROR_CODES = {
  caseLocked: CATALOG_ERROR_CODES.caseLocked,
  lockNotHeld: CATALOG_ERROR_CODES.lockNotHeld,
} as const;

/**
 * Shown when the lock endpoints report a holder without a resolvable name
 * (GET /lock returns ids only; the 409 body carries the name).
 */
const UNKNOWN_HOLDER_NAME = "another user";

/**
 * Stable per-tab session id, persisted in `sessionStorage`. Each tab gets its
 * own UUID at first call; the same tab reuses it across navigations (giving
 * the case-db engine proper re-entrancy on the lock and the
 * transition-time `lockService.holds(...)` check).
 *
 * The case-db endpoints accept a missing `X-Session-Id` (header is
 * `required = false`), but threading a real per-tab UUID lets the engine
 * distinguish multiple tabs of the same user.
 */
const SESSION_ID_STORAGE_KEY = "session_id";

export function getOrCreateSessionId(): string {
  if (typeof window === "undefined") return "no-session";
  let id = sessionStorage.getItem(SESSION_ID_STORAGE_KEY);
  if (!id) {
    id = generateUuid();
    sessionStorage.setItem(SESSION_ID_STORAGE_KEY, id);
  }
  return id;
}

/**
 * The generated client adds a `User-Agent` header, which no browser lets a
 * page set — every call logs "Refused to set unsafe header" and the header
 * never goes anyway. Passing our own empty header set replaces theirs,
 * because the constructor spreads `baseOptions` after its own defaults.
 * Same fix as `catalogSectionService.ts`.
 */


async function lockApi(): Promise<CaseLockControllerApi> {
  return catalogApiClient(CaseLockControllerApi);
}

async function workflowStatusApi(): Promise<CaseWorkflowStatusControllerApi> {
  return catalogApiClient(CaseWorkflowStatusControllerApi);
}

/**
 * Acquire the case-db edit lock. On success the case is locked by this user;
 * if the case was pooled, the lock service self-assigns it (engine semantic
 * from #1688). On 409 (CASE_LOCKED), returns the holder info so the UI can
 * render the conflict modal without a follow-up `GET /lock` round-trip.
 */
export async function acquireEditLock(caseId: string): Promise<LockResult> {
  try {
    await (await lockApi()).acquire(Number(caseId), getOrCreateSessionId());
    return { ok: true };
  } catch (err: any) {
    if (
      err?.response?.status === 409 &&
      err.response.data?.errorCode === LOCK_ERROR_CODES.caseLocked
    ) {
      const data = err.response.data?.data;
      return {
        ok: false,
        message: err.response.data?.message ?? "case is locked",
        holder: data
          ? {
              lockedBy: data.locked_by,
              lockedByName: data.locked_by_name,
              expiresAt: data.expires_at,
            }
          : null,
      };
    }
    throw err;
  }
}

/**
 * Heartbeat renewal of the case-db edit lock (issue #1757 / #1980). Calls
 * `POST /api/v1/cases/{id}/lock/refresh` — a pure per-user extend server-side:
 * it never creates a lock and never steals one. On 409 (LOCK_NOT_HELD) the
 * lock has lapsed (expired/swept, or taken over); the caller decides how to
 * recover (see `useLockHeartbeat`). Any other failure is rethrown so
 * transient network errors are distinguishable from a lost lock.
 */
export async function refreshEditLock(caseId: string): Promise<RefreshLockResult> {
  try {
    await (await lockApi()).refresh(Number(caseId), getOrCreateSessionId());
    return { ok: true };
  } catch (err: any) {
    if (
      err?.response?.status === 409 &&
      err.response.data?.errorCode === LOCK_ERROR_CODES.lockNotHeld
    ) {
      return {
        ok: false,
        message: err.response.data?.message ?? "lock is not held by this session",
      };
    }
    throw err;
  }
}

/**
 * Current active lock on a case (`GET /api/v1/cases/{id}/lock`), or null when
 * nobody holds one. The GET endpoint returns ids only (no holder name — that
 * travels on the 409 body), so `lockedByName` falls back to a generic label
 * the LockConflictModal / ReadOnlyBar can render as-is.
 */
export async function getEditLock(caseId: string): Promise<LockHolder | null> {
  const res = await (await lockApi()).current(Number(caseId));
  // LockView serializes camelCase (plain Java record, no naming overrides) —
  // unlike the 409 CASE_LOCKED body, which is snake_case. Verified live.
  const data = res.data?.data;
  if (!data || data.lockedBy == null) return null;
  return {
    lockedBy: data.lockedBy,
    lockedByName: UNKNOWN_HOLDER_NAME, // LockView carries ids only, never a name
    expiresAt: data.expiresAt ?? "",
  };
}

/**
 * Bare current workflow stage of a case (`GET /api/v1/cases/{id}/workflow-status`
 * → `current_stage`, e.g. "QualityReview"). Lives here rather than in
 * caseReview.ts because its one consumer is the heartbeat's stale-view check:
 * after a lapsed lock, `useLockHeartbeat` compares the stage now against the
 * stage it saw at mount to decide between silent re-acquire and a reload. The
 * case-review route also uses it to retain the original Submission workspace.
 */
export async function fetchCurrentWorkflowStage(caseId: string): Promise<string | null> {
  const res = await (await workflowStatusApi()).workflowStatus(Number(caseId));
  return res.data?.data?.current_stage ?? null;
}

/**
 * Admin override (issue #1698). Posts to `POST /api/v1/cases/{id}/lock/override`
 * with the audit reason (the case-db engine writes a LOCK_OVERRIDDEN event).
 * On success the lock is cleared; the caller is expected to retry
 * `acquireEditLock` to claim it.
 */
export async function overrideEditLock(
  caseId: string,
  reason: string,
): Promise<void> {
  await (await lockApi()).override(Number(caseId), getOrCreateSessionId(), { reason });
}

/** Explicit edit-session release using the existing backend contract. */
export async function releaseEditLock(caseId: string): Promise<void> {
  await (await lockApi()).release1(Number(caseId), getOrCreateSessionId());
}
